const PAY_LINK_TOKEN_KEY_PREFIX = "pay_link_token:";

/** sessionStorage 備援值的存活時間。過短會在配送員出示付款碼到顧客實際完成 Apple Pay
 * 之間就失效（不合理地把使用者踢回「連結無效」）；過長則放大「刷新後還能重放已用過網址」
 * 的視窗。30 分鐘對齊付款這類短流程的合理耐心上限。 */
const PAY_LINK_TOKEN_TTL_MS = 30 * 60 * 1000;

type StoredPayLinkToken = { token: string; exp: number };

/** 從 `/pay/:orderId` 解析 orderId；非此路徑格式回 null。 */
const parsePayOrderId = (pathname: string): string | null => {
  const m = /^\/pay\/([^/]+)$/.exec(pathname);
  return m ? m[1] : null;
};

/** 解析 sessionStorage 存的 JSON（{token, exp}）；格式錯誤或已過期一律視為沒有備援值。 */
const parseStoredToken = (raw: string): string | null => {
  try {
    const parsed = JSON.parse(raw) as Partial<StoredPayLinkToken>;
    if (typeof parsed.token !== "string" || parsed.token.length === 0 || typeof parsed.exp !== "number") return null;
    if (Date.now() >= parsed.exp) return null;
    return parsed.token;
  } catch {
    return null;
  }
};

const readSessionToken = (orderId: string): string | null => {
  try {
    const raw = window.sessionStorage.getItem(`${PAY_LINK_TOKEN_KEY_PREFIX}${orderId}`);
    return raw ? parseStoredToken(raw) : null;
  } catch {
    // sessionStorage 不可用（無痕模式等）時安靜失敗
    return null;
  }
};

/**
 * 一次性付款連結（Apple Pay 掃碼／外開 Safari）：`/pay/:orderId?pt=<token>`，無 LINE 登入。
 * query 讀不到 pt 時（頁面讀完會用 history.replaceState 把它從網址列移除，
 * 重新整理或任何重新求值都會讀不到 query 版本）退回 sessionStorage 備援——
 * 由 storePayLinkToken 在第一次拿到 pt 當下存入，需知道 orderId 才能找回；
 * 備援值有 30 分鐘 TTL，過期視為沒有（見 PAY_LINK_TOKEN_TTL_MS）。
 */
export const getPayLinkToken = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): string | null => {
  const v = new URLSearchParams(search).get("pt");
  if (v && v.length > 0) return v;
  if (typeof window === "undefined") return null;
  const orderId = parsePayOrderId(pathname);
  return orderId ? readSessionToken(orderId) : null;
};

export const isPayLinkMode = (
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
  search: string = typeof window !== "undefined" ? window.location.search : "",
): boolean => /^\/pay\/[^/]+$/.test(pathname) && getPayLinkToken(search, pathname) !== null;

/**
 * 拿到 pt 後立即存起來，供 history.replaceState 清掉網址列後（重新整理／任何重新求值）備援讀取。
 * 存 `{token, exp}`（30 分鐘 TTL）而非裸 token：避免「重新整理掉進 LIFF 登入」與「備援值無限期有效」。
 */
export const storePayLinkToken = (orderId: string, token: string): void => {
  try {
    const value: StoredPayLinkToken = { token, exp: Date.now() + PAY_LINK_TOKEN_TTL_MS };
    window.sessionStorage.setItem(`${PAY_LINK_TOKEN_KEY_PREFIX}${orderId}`, JSON.stringify(value));
  } catch {
    // sessionStorage 不可用時安靜失敗，退回「重新整理即斷」的原本行為
  }
};

/** 付款完成／連結失效（link_expired、PAY_TOKEN_USED）後清掉備援值，避免之後誤用已失效的 token。 */
export const clearPayLinkToken = (orderId: string): void => {
  try {
    window.sessionStorage.removeItem(`${PAY_LINK_TOKEN_KEY_PREFIX}${orderId}`);
  } catch {
    // noop
  }
};

/**
 * 把 URL／文字中的 `pt=<值>`（一次性付款連結 token）過濾掉，供 Sentry beforeSend 清洗
 * event.request.url 與 breadcrumb 的 from/to/url，避免 token 外洩到錯誤回報系統。
 * 只替換 pt 的值，其餘 query 參數原樣保留；沒有 pt 就原樣傳回。
 */
export const scrubPayLinkToken = (url: string): string => url.replace(/([?&]pt=)[^&#]+/g, "$1[Filtered]");
